import { toDate } from '@art-suite/art-core-ts';

import { getLineItemFieldChanges, type NamedModifier, type PresentableLineItem, type ScanPartFieldChange } from '@kaiizen/kai-presenters';
import type { ScanPartHistoryRecord } from '@kaiizen/kai-pro-api';
import type { ScanTaskOptimized, ScanTemplateValues } from '@kaiizen/kai-scan-template-system';

import { getLocalDayKey } from './activityLogTableLib';
import { getScanPartFieldChanges, isModifierChange } from './scanPartVersionChangesLib';
import { getLineItemName, getTaskTitle } from './scopeNavigatorPartPresentersLib';

export type TaskEditKind = 'added' | 'updated' | 'removed';

type TaskEditPartType = 'task' | 'option' | 'lineItem';

/** One saved edit to a task, one of its options, or one of their line items, in a task's activity log. */
export type TaskEditRow = {
  id: string;
  at: Date;
  actorUserId?: string;
  scanPartId: string;
  partType: TaskEditPartType;
  kind: TaskEditKind;
  /** The part's display name: the task's title, the option's name, or the line item's product (else its own name). */
  partName: string;
  /** What this version changed from the one before it (what it was added with, for a new part). */
  changes: ScanPartFieldChange[];
  /** The changes a person would see on the part: a line item's scope-row fields; every change for the task and options. */
  visibleChanges: ScanPartFieldChange[];
};

/** A task part's display name and value templates, as the task shows it now. */
export type TaskPartInfo = { name?: string; valueTemplates?: ScanTemplateValues };

const TASK_EDIT_PART_TYPES = new Set<string>(['task', 'option', 'lineItem']);

const isTaskEditPart = (record: ScanPartHistoryRecord): record is ScanPartHistoryRecord & { type: TaskEditPartType } => TASK_EDIT_PART_TYPES.has(record.type);

/**
 * The task, its options and their line items, by scanPartId: each one's name and value templates. Line
 * items the task's template defines are listed by their template's scanPartId too, so a line item no
 * longer on the task can still be named from the template it was made from.
 */
export const getTaskPartInfoByScanPartId = (task: ScanTaskOptimized): Map<string, TaskPartInfo> =>
  new Map<string, TaskPartInfo>([
    ...(task.template?.optionTemplates ?? []).flatMap(optionTemplate => optionTemplate.lineItemTemplates ?? [])
      .map(lineItemTemplate => [lineItemTemplate.id, { name: lineItemTemplate.name, valueTemplates: lineItemTemplate.values }] as const),
    [task.id, { name: getTaskTitle(task), valueTemplates: task.template?.values }],
    ...task.options.map(option => [option.id, { name: option.template?.name?.trim() || 'Option', valueTemplates: option.template?.values }] as const),
    ...task.options.flatMap(option => option.lineItems).map(lineItem =>
      [lineItem.id, { name: getLineItemName(lineItem), valueTemplates: lineItem.template?.values }] as const
    ),
  ]);

/** A part no longer on the task, named from its own versions: the product it held, else the template it was made from. */
const getRemovedPartInfo = (versions: readonly (ScanPartHistoryRecord | undefined)[], partInfoByScanPartId: ReadonlyMap<string, TaskPartInfo>) => {
  const productTitle = versions.map(version => (version?.data as { skuDetails?: { title?: string } } | undefined)?.skuDetails?.title?.trim()).find(Boolean);
  const templatePartInfo = partInfoByScanPartId.get(versions.find(version => version?.templateScanPartId)?.templateScanPartId ?? '');
  return { name: productTitle || templatePartInfo?.name || 'Unnamed item', valueTemplates: templatePartInfo?.valueTemplates };
};

/**
 * Pairs each version with the one before it for the same part, given a newest-first history: walking
 * oldest to newest, the previous version is the last one seen for that scanPartId.
 */
const getVersionsWithPrevious = (scanPartHistory: readonly ScanPartHistoryRecord[]) => {
  const latestSeenByScanPartId = new Map<string, ScanPartHistoryRecord>();
  return [...scanPartHistory].reverse().map(current => {
    const previous = latestSeenByScanPartId.get(current.scanPartId);
    latestSeenByScanPartId.set(current.scanPartId, current);
    return { previous, current };
  }).reverse();
};

/** Status changes already have their own rows; a task version that only moved its work status adds nothing. */
const isWorkStatusOnly = (partType: TaskEditPartType, changes: readonly ScanPartFieldChange[]) =>
  partType === 'task' && changes.every(change => change.field === 'Work status');

/**
 * A task's edits as activity-log rows, newest first: one row per saved version of the task, its options
 * and their line items. A part's oldest version is its "added" row, a version marked deleted is
 * "removed", and the rest are "updated", each carrying the fields it changed. Left out: versions that
 * changed nothing visible (for a line item, none of its scope row's fields), task versions that only
 * changed the work status, and an option's creation and removal (its line items' own rows say what
 * changed). Parts no longer on the task fall back to a
 * generic name.
 *
 * @param scanPartHistory - the task's full history (every version of it and its descendants), newest first
 */
export const getTaskEditRows = ({ scanPartHistory, partInfoByScanPartId, contractorNamesById, knownModifiers = [] }: {
  scanPartHistory: readonly ScanPartHistoryRecord[];
  partInfoByScanPartId: ReadonlyMap<string, TaskPartInfo>;
  contractorNamesById: ReadonlyMap<string, string>;
  /** The org's modifiers, known rather than enabled: a disabled modifier's past edits still read by its name. */
  knownModifiers?: readonly NamedModifier[];
}): TaskEditRow[] =>
  compactTaskEditRows(
    getVersionsWithPrevious(scanPartHistory).flatMap(({ previous, current }): TaskEditRow[] => {
      if (!isTaskEditPart(current)) return [];
      const partInfo = partInfoByScanPartId.get(current.scanPartId) ?? getRemovedPartInfo([current, previous], partInfoByScanPartId);
      const kind: TaskEditKind = current.deleted ? 'removed' : previous ? 'updated' : 'added';
      if (current.type === 'option' && kind !== 'updated') return [];
      const changes = getScanPartFieldChanges(previous, current, { valueTemplates: partInfo.valueTemplates, contractorNamesById, knownModifiers });
      const visibleChanges = current.type === 'lineItem'
        ? getLineItemFieldChanges(
          (previous?.data ?? {}) as PresentableLineItem,
          (current.data ?? {}) as PresentableLineItem,
          partInfo.valueTemplates,
          knownModifiers,
        )
        : changes;
      if (kind === 'updated' && (visibleChanges.length === 0 || isWorkStatusOnly(current.type, changes))) return [];
      return [{
        id: `edit-${current.id}`,
        at: toDate(current.updatedAt),
        actorUserId: current.userId,
        scanPartId: current.scanPartId,
        partType: current.type,
        kind,
        partName: partInfo.name ?? 'Unnamed item',
        changes,
        visibleChanges,
      }];
    }),
  );

/** The one visible field an update changed, if it changed exactly one. */
const getSoleChangedField = (row: TaskEditRow) => row.kind === 'updated' && row.visibleChanges.length === 1 ? row.visibleChanges[0].field : undefined;

/** Whether `row` continues `previousRow`'s run of single-field edits: same part, same field, same person, same local day. */
const continuesFieldEditRun = (previousRow: TaskEditRow, row: TaskEditRow) => {
  const field = getSoleChangedField(row);
  return field !== undefined
    && field === getSoleChangedField(previousRow)
    && row.scanPartId === previousRow.scanPartId
    && row.actorUserId === previousRow.actorUserId
    && getLocalDayKey(row.at) === getLocalDayKey(previousRow.at);
};

/** Each field's change across a run, newest edit first: from its value before the run's first edit of it to its value after the last. */
const mergeChangesAcrossRun = (runNewestFirst: readonly TaskEditRow[], getChanges: (row: TaskEditRow) => ScanPartFieldChange[]) => {
  const mergedByField = new Map<string, ScanPartFieldChange>();
  for (const row of runNewestFirst) {
    for (const change of getChanges(row)) {
      const newerChange = mergedByField.get(change.field);
      mergedByField.set(change.field, newerChange ? { ...newerChange, before: change.before } : change);
    }
  }
  return [...mergedByField.values()];
};

/**
 * Compacts each run of consecutive edits that change the same one field of the same part (by one
 * person, within one day in the viewer's time zone) into a single edit: at the last edit's time, from
 * the field's value before the first to its value after the last. Typing "125" into a quantity saves
 * three edits; the log shows one, "Qty: 1 → 125". A run that ends where it started ("Qty: 2 → 2") is
 * dropped: on balance it changed nothing.
 *
 * @param rows - newest first
 */
export const compactTaskEditRows = (rows: readonly TaskEditRow[]): TaskEditRow[] => {
  const result: TaskEditRow[] = [];
  let run: TaskEditRow[] = [];
  const flushRun = () => {
    if (run.length > 1) {
      const visibleChanges = mergeChangesAcrossRun(run, row => row.visibleChanges);
      // a run that ends where it started (2 → 3 → 2) changed nothing: it leaves the log
      if (visibleChanges.some(({ before, after }) => before !== after)) {
        result.push({ ...run[0], changes: mergeChangesAcrossRun(run, row => row.changes), visibleChanges });
      }
    } else result.push(...run);
    run = [];
  };
  for (const row of rows) {
    if (run.length && !continuesFieldEditRun(run[run.length - 1], row)) flushRun();
    run.push(row);
  }
  flushRun();
  return result;
};

/** An edit that added or removed a modifier: a change-order trigger, whenever it happens. */
export const hasModifierChange = ({ visibleChanges }: TaskEditRow) => visibleChanges.some(isModifierChange);

/** A task edit that only changed its contractor: it reads "Contractor assigned", over the new contractor. */
export const isContractorAssignment = ({ partType, kind, changes }: TaskEditRow) =>
  partType === 'task' && kind === 'updated' && changes.length === 1 && changes[0].field === 'Contractor';

/** The task's own first version: the log's opening row, which the design reads simply as "Created". */
export const isTaskCreation = ({ partType, kind }: TaskEditRow) => partType === 'task' && kind === 'added';

/** A row's headline: the part's name and what happened to it ("Cabinet pulls updated"), or "Contractor assigned", or "Created" for the task itself. */
export const getTaskEditName = (row: TaskEditRow) => {
  if (isTaskCreation(row)) return 'Created';
  return isContractorAssignment(row) ? 'Contractor assigned' : `${row.partName} ${row.kind}`;
};
